import React, { useContext } from "react";
import { User } from "models/User";
import { useState } from "react";
import { AuthService } from "services/AuthService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Crisp } from "crisp-sdk-web";
import { ImageData, useImage } from "./ProfileImageProvider";
import { UserTypes } from "enum/UserTypes";
import { connectSocket, disconnectSocket } from "services/socket";

export type AuthData = [
  User | undefined,
  (user: User | undefined) => void,
  () => void,
  boolean,
  () => Promise<User | undefined>
];

const AuthContext = React.createContext<AuthData>([undefined, () => {}, () => {}, true, async () => undefined]);

const hasValidToken = () => {
  const token = localStorage.getItem("token");
  return !!token && !["null", "undefined", "", "[object Object]"].includes(token.trim()) && token.trim().split(".").length === 3;
};

export const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [image, setImage] = useImage() as ImageData;
  const navigate = useNavigate();

  const clearCrispSessionLocalStorage = () => {
    const crispSessionPrefix = "crisp-client/session/";

    // Get all keys from local storage
    const keys = Object.keys(localStorage);

    // Filter and remove keys that match the Crisp session prefix
    keys.forEach(key => {
      if (key.startsWith(crispSessionPrefix)) {
        localStorage.removeItem(key);
      }
    });
  };

  const refreshUser = React.useCallback(async () => {
    if (!hasValidToken()) {
      setUser(undefined);
      setAuthLoading(false);
      disconnectSocket();
      return undefined;
    }

    setAuthLoading(true);
    try {
      const res = await AuthService.getMe();

      if (res.success) {
        setUser(res.data);
        connectSocket(res.data?._id);
        return res.data;
      }

      localStorage.removeItem("token");
      setUser(undefined);
      disconnectSocket();
      return undefined;
    } catch (error) {
      localStorage.removeItem("token");
      setUser(undefined);
      disconnectSocket();
      return undefined;
    } finally {
      setAuthLoading(false);
    }
  }, []);
 
  React.useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  React.useEffect(() => {
    if (user && user.verifiedStatus === "deactive") {
      navigate(`/signin`, { replace: true });
      localStorage.removeItem("token");
      setUser(undefined);
      setImage({});
      disconnectSocket();
      // window.location.reload();
      
    }
  }, [user?.verifiedStatus]); 
  const logout = () => {
    localStorage.removeItem("token");
    // Explicit staff logout must not leave a stashed admin session behind.
    AuthService.clearStaffSessionStash();
    clearCrispSessionLocalStorage();
    Crisp.session.reset();
    Crisp.chat.close();
    if (user?.role !== UserTypes.SUPER_ADMIN) {
      Crisp.chat.hide();
    }
    if (user?._id) {
    localStorage.removeItem(`dismissedBroadcasts_${user._id}`);
  }
    setUser(undefined);
    setImage({});
    disconnectSocket();
    window.location.replace("/signin");
  };

  return <AuthContext.Provider value={[user, setUser, logout, authLoading, refreshUser]}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};

type UserData = [User | undefined, (user: User | undefined) => void];
const UserContext = React.createContext<UserData>([undefined, () => ({})]);

export default UserContext;
